import React, { useState } from 'react';
import { Box, Stack, TextField, MenuItem, IconButton, Typography, Alert } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import EditorialButton from '../editorial/EditorialButton';
import { editorialInputSx } from '../editorial/editorialInputStyles';
import MembershipCheckout from '../membership/MembershipCheckout';
import { RETURN_PATHS } from '../../service/checkoutReturn';
import {
  validateIncludedSeats,
  validateGuestSeats,
  validateSeatSections,
  computeMemberSeatCost,
  seatRowsToBlock,
  seatBlockToRows,
} from '../../service/highHolidaySeats';

const OFFICE_EMAIL = 'office@kbtla.org';

const cardSx = {
  p: { xs: 3, md: 4 },
  border: '1px solid rgba(36,34,88,0.15)',
  background: 'var(--paper)',
};

// Purchased seats are deliberately a different surface from the editable
// included ones: tinted, borderless, no fields. A member should be able to
// tell at a glance which seats they can still change and which are paid.
const paidCardSx = {
  p: { xs: 2.5, md: 3 },
  border: '1px solid rgba(36,34,88,0.08)',
  background: 'rgba(36,34,88,0.04)',
};

const sectionHeadingSx = { display: 'block' };

const sectionLabel = (gender) => (gender === 'female' ? "Women's section" : "Men's section");

// One name + section row. Deliberately the same shape as the signup
// flow's SeatForm#SeatPersonRow — a member who enrolled through the site
// has already filled this row in once, and giving them a second, different
// row for the same job is how a form starts feeling unfamiliar.
//
// The one difference: the section select starts UNSET rather than
// defaulting to men's. SeatForm defaults it, which is safe there because
// its own validation demands a name for every row; here an unset section
// is what makes validateSeatSections ask, and seating is separate enough
// that guessing could seat someone on the wrong side.
function SeatNameRow({ row, label, onChange, onRemove, disabled = false }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
      {/* flex: 1 + minWidth: 0 is what makes this the field that grows to
          fill the row. Without minWidth: 0 a flex item's min-width is its
          content size, which fights flex-shrink and — once a third sibling
          (the remove button) is in the row — collapses this field to
          nearly nothing. Same fix as SeatForm. */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <TextField
          label={label}
          value={row.name}
          onChange={(e) => onChange({ ...row, name: e.target.value })}
          disabled={disabled}
          sx={editorialInputSx}
          fullWidth
        />
      </Box>
      <TextField
        select
        label="Seating"
        value={row.gender || ''}
        onChange={(e) => onChange({ ...row, gender: e.target.value })}
        disabled={disabled}
        SelectProps={{ displayEmpty: true }}
        InputLabelProps={{ shrink: true }}
        sx={{ ...editorialInputSx, minWidth: { sm: 190 }, flexShrink: 0 }}
        fullWidth={false}
      >
        <MenuItem value="">Choose…</MenuItem>
        <MenuItem value="male">Men's section</MenuItem>
        <MenuItem value="female">Women's section</MenuItem>
      </TextField>
      {onRemove && (
        // width/minWidth overridden explicitly: this repo has a legacy
        // global `button { width: 100%; }` rule (src/styles/App.css) that
        // otherwise stretches every raw <button> — MUI's IconButton
        // included — to fill its flex container.
        <IconButton
          onClick={onRemove}
          disabled={disabled}
          aria-label="Remove guest seat"
          sx={{ mt: { sm: 0.5 }, flexShrink: 0, width: 40, minWidth: 40 }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      )}
    </Stack>
  );
}

// The High Holiday section of the member portal: confirm whether the
// household is coming, name any seats the membership includes, and buy
// guest seats. Members pay the member rate; everyone else pays the
// public rate and is pointed at /membership2026.
//
// Everything numeric or rule-shaped in here comes from
// service/highHolidaySeats.js, which mirrors the backend's own rules — the
// backend stays the authority on both the allowance and the price.
export default function AccountSeatsForm({
  highHolidays,
  onConfirmAttendance,
  onStartSeatPurchase,
  onCancelSeatPurchase,
  clientSecret = null,
  saving = false,
  error = null,
  returnedFromPayment = false,
}) {
  // Local edits to the included-seat names. Null until the member types,
  // so a fresh load (or a save echoing back from the server) is what's
  // shown until then — no effect needed to keep the two in sync.
  const [includedDraft, setIncludedDraft] = useState(null);
  const [guestRows, setGuestRows] = useState([]);
  // Null means "no answer given yet in this session" — the server's own
  // `attending` is used until the member picks one.
  const [attendingChoice, setAttendingChoice] = useState(null);
  const [includedError, setIncludedError] = useState(null);

  if (highHolidays?.accountPending) {
    return (
      <Box sx={cardSx} maxWidth={680}>
        <Typography color="text.secondary">
          Setting up your account… Refresh this page in a moment if this doesn't clear on its own.
        </Typography>
      </Box>
    );
  }

  if (!highHolidays) {
    return (
      <Box sx={cardSx} maxWidth={680}>
        <Typography color="text.secondary">
          We couldn't load your High Holiday details just now. Please refresh the page — or email
          the office at{' '}
          <Box component="span" sx={{ color: 'var(--teal-500)' }}>
            {OFFICE_EMAIL}
          </Box>{' '}
          and we'll take care of it for you.
        </Typography>
      </Box>
    );
  }

  const allowance = Number.isFinite(highHolidays.allowance) ? highHolidays.allowance : 0;
  const isMember = highHolidays.isMember === true;
  const extraSeatPrice = Number.isFinite(highHolidays.extraSeatPrice)
    ? highHolidays.extraSeatPrice
    : highHolidays.memberSeatPrice;
  const memberRate = Number.isFinite(highHolidays.memberSeatPrice)
    ? highHolidays.memberSeatPrice
    : extraSeatPrice;

  // `attending` is null on the server until the member answers, which is
  // exactly the "never confirmed" state this screen opens in.
  const serverAttending =
    typeof highHolidays.attending === 'boolean' ? highHolidays.attending : null;
  const attending = attendingChoice !== null ? attendingChoice : serverAttending;
  const confirmed = serverAttending !== null;

  // Built from `includedSeats` ONLY — never from `hhSeats`, which is the
  // merge of included + purchased. Feeding the merged block in here is
  // precisely how a paid seat would end up in an editable field and get
  // rewritten as an included one; the backend rejects that, and the UI
  // should never ask for it in the first place.
  const includedRows = includedDraft || seatBlockToRows(highHolidays.includedSeats, allowance);
  const purchasedRows = seatBlockToRows(highHolidays.purchasedSeats);

  // What actually gets sent and charged, so the total on screen can never
  // differ from the amount on the PaymentIntent.
  const guestBlock = seatRowsToBlock(guestRows);
  const guestSeatCount = guestBlock.menSeatNames.length + guestBlock.womenSeatNames.length;
  const cost = computeMemberSeatCost(guestSeatCount, extraSeatPrice);
  const guestCheck = validateGuestSeats(guestRows);

  // computeMemberSeatCost floors an absent or malformed memberSeatPrice to
  // 0 rather than rendering "$NaN". A $0 guest seat is not a real price, so
  // rather than show one, this hides the purchase path and points at the
  // office.
  const priceKnown = cost.pricePerSeat > 0;

  // Once a PaymentIntent exists it is for a specific number of seats at a
  // specific amount. Editing the rows underneath it would leave the screen
  // showing one total while Stripe charges another, so the rows lock until
  // the member either pays or backs out.
  const awaitingPayment = Boolean(clientSecret);

  const updateIncluded = (index, row) => {
    setIncludedError(null);
    setIncludedDraft(includedRows.map((r, i) => (i === index ? row : r)));
  };

  const handleAttendanceSubmit = (attendingValue) => {
    setIncludedError(null);

    if (!attendingValue) {
      // The backend clears included seats when attending is false (paid
      // seats are untouched — they're paid), so there is nothing to send.
      onConfirmAttendance({ attending: false, menSeatNames: [], womenSeatNames: [] });
      return;
    }

    const countCheck = validateIncludedSeats(
      includedRows.map((r) => r.name),
      allowance
    );
    if (!countCheck.valid) {
      setIncludedError(countCheck.error);
      return;
    }

    // A named row with no section would be dropped silently by
    // seatRowsToBlock — the member types a name, nothing is sent, and their
    // seat isn't there. Ask instead.
    const sectionCheck = validateSeatSections(includedRows);
    if (!sectionCheck.valid) {
      setIncludedError(sectionCheck.error);
      return;
    }

    onConfirmAttendance({ attending: true, ...seatRowsToBlock(includedRows) });
  };

  const addGuestRow = () => setGuestRows([...guestRows, { name: '', gender: '' }]);
  const updateGuestRow = (index, row) =>
    setGuestRows(guestRows.map((r, i) => (i === index ? row : r)));
  const removeGuestRow = (index) => setGuestRows(guestRows.filter((_, i) => i !== index));

  return (
    <Stack spacing={3} maxWidth={720}>
      {error && <Alert severity="error">{error}</Alert>}

      {!isMember && (
        <Box
          sx={{
            ...cardSx,
            border: '3px solid var(--teal-500)',
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: 'var(--teal-500)',
              mb: 1,
            }}
          >
            Not a member yet
          </Typography>
          <Typography
            sx={{
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              fontSize: { xs: 20, md: 23 },
              color: 'var(--navy-500)',
              mb: 1,
            }}
          >
            Become a member for included seats and the ${memberRate} rate
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            High Holiday seats are ${Number.isFinite(extraSeatPrice) ? extraSeatPrice : 360} each
            without a membership. Members get seats included with their tier, and extra guest seats
            at the member rate.
          </Typography>
          <EditorialButton variant="primary" href="/membership2026">
            Become a member
          </EditorialButton>
        </Box>
      )}

      {/* Back from Stripe. The seats are written by the payment webhook, so
          for a moment after a successful payment there is genuinely nothing
          to show — and an empty "no guest seats" state at that exact moment
          reads as though the money vanished. */}
      {returnedFromPayment && (
        <Alert severity={purchasedRows.length > 0 ? 'success' : 'info'}>
          {purchasedRows.length > 0
            ? 'Payment received — your guest seats are listed below.'
            : 'Payment received — your seats will appear here shortly.'}
        </Alert>
      )}

      {/* ---------- Attendance (members only) ---------- */}
      {isMember && (
        <Box sx={cardSx}>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="overline" color="text.secondary" sx={sectionHeadingSx}>
                Attendance
              </Typography>
              <Typography
                sx={{
                  mt: 0.5,
                  fontFamily: 'var(--font-display)',
                  fontWeight: 700,
                  fontSize: { xs: 20, md: 23 },
                  lineHeight: 1.25,
                  color: 'var(--navy-500)',
                }}
              >
                Will your household be joining us for the High Holidays?
              </Typography>
              {confirmed && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {serverAttending
                    ? "You're confirmed. You can change any of this below whenever you like."
                    : "You've told us you won't be joining this year. If that changes, just let us know here."}
                </Typography>
              )}
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <EditorialButton
                variant={attending === true ? 'primary' : 'ghost'}
                onClick={() => setAttendingChoice(true)}
                disabled={saving}
              >
                {attending === true ? "Yes — we'll be there ✓" : "Yes, we'll be there"}
              </EditorialButton>
              <EditorialButton
                variant={attending === false ? 'secondary' : 'ghost'}
                onClick={() => setAttendingChoice(false)}
                disabled={saving}
              >
                {attending === false ? 'Not this year ✓' : 'Not this year'}
              </EditorialButton>
            </Stack>

            {attending === false && (
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  We'll take your included seats out of the seating plan so someone else can use
                  them. Any guest seats you've already paid for stay yours.
                </Typography>
                <EditorialButton
                  variant="secondary"
                  onClick={() => handleAttendanceSubmit(false)}
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Save — not this year'}
                </EditorialButton>
              </Stack>
            )}

            {attending === true && allowance === 0 && serverAttending !== true && (
              <EditorialButton
                variant="primary"
                onClick={() => handleAttendanceSubmit(true)}
                disabled={saving}
                fullWidth
              >
                {saving ? 'Saving…' : 'Confirm attendance'}
              </EditorialButton>
            )}
          </Stack>
        </Box>
      )}

      {/* ---------- Included seats (members only) ---------- */}
      {isMember && attending === true && allowance > 0 && (
        <Box sx={cardSx}>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="overline" color="text.secondary" sx={sectionHeadingSx}>
                Included seats — no charge
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {allowance > 0
                  ? `Your membership includes ${allowance} High Holiday seat${
                      allowance === 1 ? '' : 's'
                    } at no charge. Tell us who's sitting in ${
                      allowance === 1 ? 'it' : 'them'
                    }, and whether each seat is in the men's or women's section.`
                  : "Your membership doesn't include any High Holiday seats. You can add guest seats below at the member rate."}
              </Typography>
            </Box>

            {includedRows.map((row, index) => (
              <SeatNameRow
                key={`included-${index}`}
                label={`Included seat ${index + 1} name`}
                row={row}
                onChange={(next) => updateIncluded(index, next)}
                disabled={saving}
              />
            ))}

            {includedError && (
              <Typography color="error" variant="body2">
                {includedError}
              </Typography>
            )}

            <Typography variant="body2" color="text.secondary">
              You can leave a name blank for now and come back to it.
            </Typography>

            <EditorialButton
              variant="primary"
              onClick={() => handleAttendanceSubmit(true)}
              disabled={saving}
              fullWidth
            >
              {saving
                ? 'Saving…'
                : serverAttending === true
                ? 'Save these seats'
                : 'Confirm attendance'}
            </EditorialButton>
          </Stack>
        </Box>
      )}

      {/* ---------- Purchased seats (read-only) ---------- */}
      {purchasedRows.length > 0 && (
        <Box sx={paidCardSx}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="overline" color="text.secondary" sx={sectionHeadingSx}>
                Guest seats you've paid for
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                These {purchasedRows.length === 1 ? 'seat is' : 'seats are'} paid and reserved. To
                change a name or a section on a paid seat, email the office at{' '}
                <Box component="span" sx={{ color: 'var(--teal-500)' }}>
                  {OFFICE_EMAIL}
                </Box>{' '}
                and we'll do it for you.
              </Typography>
            </Box>

            <Stack spacing={1}>
              {purchasedRows.map((row, index) => (
                <Stack
                  key={`purchased-${index}`}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 0.25, sm: 1.5 }}
                  alignItems={{ sm: 'baseline' }}
                >
                  <Typography sx={{ fontFamily: 'var(--font-body)', fontWeight: 700, color: 'var(--navy-700)' }}>
                    {row.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 11,
                      letterSpacing: '0.15em',
                      textTransform: 'uppercase',
                      color: 'var(--navy-300)',
                    }}
                  >
                    {sectionLabel(row.gender)} · Paid
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Box>
      )}

      {/* ---------- Buy seats ---------- */}
      {(isMember ? attending !== false && (serverAttending === true || purchasedRows.length > 0) : true) && (
        <Box sx={cardSx}>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="overline" color="text.secondary" sx={sectionHeadingSx}>
                {isMember ? 'Guest seats' : 'Non Member Seats'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {isMember
                  ? `For family or friends joining you${
                      allowance > 0 ? ' beyond your included seats' : ''
                    }.${
                      priceKnown
                        ? ` Each guest seat is $${cost.pricePerSeat} at the member rate.`
                        : ''
                    }`
                  : priceKnown
                  ? `Each seat is $${cost.pricePerSeat} for you and your family.`
                  : null}
              </Typography>
            </Box>

            {!priceKnown ? (
              <Alert severity="info">
                We can't show guest-seat pricing right now. Email the office at {OFFICE_EMAIL} and
                we'll add your guest seats for you.
              </Alert>
            ) : (
              <>
                {guestRows.map((row, index) => (
                  <SeatNameRow
                    key={`guest-${index}`}
                    label={`${isMember ? 'Guest seat' : 'Seat'} ${index + 1} name`}
                    row={row}
                    onChange={(next) => updateGuestRow(index, next)}
                    onRemove={awaitingPayment ? undefined : () => removeGuestRow(index)}
                    disabled={awaitingPayment}
                  />
                ))}

                {/* Shown live rather than only on click, because the buy
                    button is disabled while the rows are incomplete — a
                    disabled button with no explanation is a dead end. This
                    is where validateGuestSeats' "choose men's or women's
                    seating for: X" reaches the member. */}
                {guestRows.length > 0 && !guestCheck.valid && (
                  <Typography color="error" variant="body2">
                    {guestCheck.error}
                  </Typography>
                )}

                {!awaitingPayment && (
                  <EditorialButton variant="ghost" onClick={addGuestRow} sx={{ alignSelf: 'flex-start' }}>
                    {isMember
                      ? `+ Add ${guestRows.length > 0 ? 'another' : 'a'} guest seat`
                      : guestRows.length > 0
                      ? '+ Add a Seat'
                      : 'Add a Seat'}
                  </EditorialButton>
                )}

                {cost.seatCount > 0 && (
                  <Box sx={{ pt: 1.5, borderTop: '1px solid rgba(36,34,88,0.1)' }}>
                    <Typography variant="body2" color="text.secondary">
                      {cost.seatCount} guest seat{cost.seatCount === 1 ? '' : 's'} × $
                      {cost.pricePerSeat} each
                    </Typography>
                    <Typography variant="body1" fontWeight={700} sx={{ mt: 0.5 }}>
                      Total: ${cost.total}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {isMember
                        ? `A one-time charge. Your ${
                            allowance > 0 ? 'included seats are' : 'membership is'
                          } never charged for this.`
                        : 'A one-time charge at the public rate.'}
                    </Typography>
                  </Box>
                )}

                {awaitingPayment ? (
                  <Stack spacing={2}>
                    <Alert severity="info">
                      Almost there — enter your card below to reserve{' '}
                      {cost.seatCount === 1 ? 'this seat' : `these ${cost.seatCount} seats`}.
                    </Alert>
                    <MembershipCheckout
                      clientSecret={clientSecret}
                      returnPath={RETURN_PATHS.extraSeats}
                      submitLabel={`Pay $${cost.total}`}
                      summary={`${cost.seatCount} guest seat${
                        cost.seatCount === 1 ? '' : 's'
                      } × $${cost.pricePerSeat}`}
                    />
                    {onCancelSeatPurchase && (
                      <EditorialButton
                        variant="ghost"
                        onClick={onCancelSeatPurchase}
                        sx={{ alignSelf: 'flex-start' }}
                      >
                        Change these seats
                      </EditorialButton>
                    )}
                  </Stack>
                ) : (
                  <EditorialButton
                    variant="primary"
                    onClick={() => onStartSeatPurchase(guestRows)}
                    disabled={!guestCheck.valid}
                    fullWidth
                  >
                    {cost.seatCount > 0
                      ? `Buy ${cost.seatCount} seat${cost.seatCount === 1 ? '' : 's'} — $${cost.total}`
                      : isMember
                      ? 'Buy guest seats'
                      : 'Buy seats'}
                  </EditorialButton>
                )}
              </>
            )}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
